import { useCallback, useEffect, useRef, useState } from "react";

import { toast } from "~/hooks/use-toast";
import {
  deleteSalesRequestDocument,
  fetchSalesRequestDocuments,
  uploadSalesRequestDocument,
} from "~/services/salesRequestDocumentService";
import { useProfileStore } from "~/store/profileStore";
import { DocumentType, SalesRequestDocument } from "~/types/client";

export const useSalesRequestDocuments = (salesRequestId: string | null) => {
  const [documents, setDocuments] = useState<SalesRequestDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { id: currentUserId } = useProfileStore();
  const isRefreshingRef = useRef(false);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    if (!salesRequestId) {
      setDocuments([]);
      return;
    }

    if (isRefreshingRef.current) {
      console.log("Already refreshing, skipping redundant fetch");
      return;
    }

    isRefreshingRef.current = true;
    setLoading(true);

    try {
      console.log("Fetching documents for sales request:", salesRequestId);
      const docs = await fetchSalesRequestDocuments(salesRequestId);
      console.log("Documents fetched:", docs.length);
      setDocuments(docs);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los documentos adjuntos.",
        variant: "destructive",
      });
    } finally {
      // Add a small delay before turning off loading state to prevent UI flicker
      setTimeout(() => {
        setLoading(false);
        isRefreshingRef.current = false;
      }, 300);
    }
  }, [salesRequestId]);

  // Upload a document
  const uploadDocument = useCallback(
    async (file: File, documentType: DocumentType): Promise<boolean> => {
      if (!salesRequestId || !currentUserId) {
        toast({
          title: "Error",
          description: "No se puede subir el documento en este momento.",
          variant: "destructive",
        });
        return false;
      }

      try {
        const result = await uploadSalesRequestDocument(
          file,
          salesRequestId,
          documentType,
          currentUserId
        );

        if (result) {
          // Add to documents list and refresh
          setDocuments((prevDocs) => [...prevDocs, result]);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error uploading document:", error);
        toast({
          title: "Error",
          description: "No se pudo subir el documento.",
          variant: "destructive",
        });
        return false;
      }
    },
    [salesRequestId, currentUserId]
  );

  // Delete a document
  const deleteDocument = useCallback(
    async (documentId: string): Promise<boolean> => {
      try {
        const result = await deleteSalesRequestDocument(documentId);
        if (result) {
          // Remove from documents list
          setDocuments((prevDocs) =>
            prevDocs.filter((doc) => doc.id !== documentId)
          );
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error deleting document:", error);
        toast({
          title: "Error",
          description: "No se pudo eliminar el documento.",
          variant: "destructive",
        });
        return false;
      }
    },
    []
  );

  // Refresh documents - now with debounce and better state management
  const refresh = useCallback(() => {
    if (isRefreshingRef.current) {
      console.log("Refresh already in progress, skipping");
      return;
    }

    console.log("Triggering document refresh");
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Fetch documents on initial load and when refreshKey changes
  useEffect(() => {
    console.log("Refresh key changed:", refreshKey);
    fetchDocuments();
  }, [fetchDocuments, refreshKey]);

  return {
    documents,
    loading,
    uploadDocument,
    deleteDocument,
    refresh,
  };
};
